import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {CompetitorResultsService} from "@services";
import {
  CategoryItem,
  CategoryType,
  CompetitorFilter,
  CompetitorResults,
  CompetitorStatus,
  EventItem,
  Lookups,
  MeasurementTypes,
  PagedFilter,
  Pages,
  Race,
  RaceSettings, ResultProcessingType,
  Waypoint,
  WaypointType
} from "@model";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {TranslocoService} from "@ngneat/transloco";
import {PagingComponent} from "../paging.component";
import {FilterGroup, FilterItem, ResultsDetailsModalComponent} from '@components';
import { FilterGroupType } from "../filter-panel/filter-panel.component";
import {getLanguageValue} from "@utility";
import {Observable, of, map, finalize} from 'rxjs';

@Component({
    selector: 'app-results-table',
    templateUrl: './results-table.component.html',
    styleUrls: ['./results-table.component.scss'],
    standalone: false
})
export class ResultsTableComponent extends PagingComponent<CompetitorResults> implements OnInit {
  public static readonly MIN_SEARCH_INPUT_LENGTH: number = 3;
  public static readonly MIN_SEARCH_INPUT_LENGTH_ADMIN: number = 1;

  @Input() event: EventItem | undefined;
  @Input() race: Race | undefined;
  @Input() filteringEnabled: boolean = true;
  @Input() showEmptyState: boolean = true;
  @Input() showBorderBottom: boolean = true;
  @Input() results: CompetitorResults[] = [];
  @Input() categories: CategoryItem[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input() genders: Lookups[] = [];
  @Input() isAdmin: boolean = false;
  @Input() showLoading: boolean = false;

  @Output() filterChange: EventEmitter<PagedFilter<CompetitorFilter>> = new EventEmitter<PagedFilter<CompetitorFilter>>();

  filter: PagedFilter<CompetitorFilter> | undefined;
  competitorFiler: CompetitorFilter = new CompetitorFilter();
  measurementType: number | undefined = undefined;
  firstCompetitorResults: CompetitorResults | undefined = undefined;
  controlWaypoints: Waypoint[] = [];
  isRound: boolean = false;
  isTeam: boolean = false;

  racesMap: Map<number, Race> = new Map<number, Race>();
  categoriesByRaceId: Map<number, CategoryItem[]> = new Map<number, CategoryItem[]>();

  filterGroupsMap: Map<number,  Map<string, FilterGroup>> = new Map<number, Map<string, FilterGroup>>();

  activeFilterGroups: Map<string, FilterGroup> | undefined;
  raceFilterGroup: FilterGroup | undefined;

  constructor(private translocoService: TranslocoService, private competitorResultsService: CompetitorResultsService, private modalService: NgbModal) {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.results.length > 0) {
      this.setFirstCompetitorResults(this.results[0]);
    }
    this.event?.racesList?.forEach(race => {
      if (race.id) {
        this.racesMap.set(race.id, race);
      }
    });
    if (this.race?.id) {
      this.categoriesByRaceId.set(this.race.id, this.categories);
      this.raceFilterGroup = this.buildRaceFilterGroup();
      this.handleRaceSelection(this.race.id, false);
    }
    this.handleExploreResult(this.results);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['results'] && !changes['results'].firstChange) {
      this.handleExploreResult(this.results);
    }
    if (changes['race'] && !changes['race'].firstChange) {
      this.setCheckpointIds();
      this.isRound = this.isRoundRace();
    }
  }


  private setFirstCompetitorResults(firstCompetitorResults: CompetitorResults | undefined) {
    this.firstCompetitorResults = firstCompetitorResults;
  }

  private setCheckpointIds() {
    if (!this.isRoundRace()) {
      const waypoints = this.race?.waypoints ?? [];

      this.controlWaypoints = waypoints
          .filter(item => item.waypointTypeId === WaypointType.CONTROL_WAYPOINT)
          .sort((a: Waypoint, b: Waypoint) => {
            const orderA = a.ordinalNumber ?? 0;
            const orderB = b.ordinalNumber ?? 0;
            return orderA - orderB;
          });
    } else {
      this.controlWaypoints = [];
    }
  }

  isRoundRace() {
    return Race.isBackyardUltra(this.race?.sport) || RaceSettings.isRoundRace(this.race?.raceSettings?.resultProcessingType)
      || !!this.firstCompetitorResults?.competitorResultsDetail?.laps;
  }

  handleRaceSelection(value: number, load: boolean = true): void {
    this.race = this.racesMap.get(value);
    this.isTeam = false;
    if (this.race) {
      /// this is going to be checked on server for public/admin handling is different
      if (this.isAdmin) {
        this.measurementType = MeasurementTypes.GUN_TIME_PRIMARY;
      } else {
        this.measurementType = this.race?.raceSettings?.measurementType;
      }
      this.isTeam = RaceSettings.isTeamRace(this.race.raceSettings);

      this.buildFilterItemsForRace(this.race, true, () => {
        if (load) {
          this.onExplore(false)
        }
        this.setCheckpointIds();
        this.isRound = this.isRoundRace();
      });
    }
  }

  protected fetchPage(page: number): Observable<CompetitorResults[]> {
    this.filter = new PagedFilter(new Pages(undefined, page), this.competitorFiler);
    this.emitFilter();
    return this.competitorResultsService.getCompetitorResults(this.filter, this.isAdmin)
  }

  protected override afterSetItems(items: CompetitorResults[], loadMore: boolean): void {
    this.results = items;
    if (items.length > 0 && !loadMore) {
      this.setFirstCompetitorResults(items[0]);
    }
  }

  protected override afterOnExplore(): void {
    this.scrollToTop();
  }

  emitFilter(): void {
    this.filterChange.emit(this.filter);
  }

  openDetailsModal(competitorResults: CompetitorResults): void {
    const isConcurrent = RaceSettings.isConcurrentRace(this.race?.raceSettings);

    if (isConcurrent && competitorResults.id) {
      this.loaderService.startLoading();
      this.competitorResultsService.getDetailedCompetitorResults(competitorResults.id).pipe(
          finalize(() => this.loaderService.dismissLoading())
      ).subscribe(detailed => {
        this.openDetailsModalWithResults(detailed ?? competitorResults);
      });
    }
    else {
      this.openDetailsModalWithResults(competitorResults);
    }
  }

  private openDetailsModalWithResults(competitorResults: CompetitorResults): void {
    const modalRef = this.modalService.open(ResultsDetailsModalComponent, {
      centered: true,
      size: 'xl',
      windowClass:'results-details-modal',
    });
    modalRef.componentInstance.competitorResults = competitorResults;
    modalRef.componentInstance.measurementType = this.competitorFiler.measurementType;
    modalRef.componentInstance.category = this.competitorFiler.categories?.length ? this.competitorFiler.categories?.at(0) : undefined;
    modalRef.componentInstance.gender = this.competitorFiler.gender;
    modalRef.componentInstance.isRoundRace = this.isRoundRace();
    modalRef.componentInstance.showDistance = this.race?.raceSettings?.resultProcessingType === ResultProcessingType.TIME_LIMITED;
    modalRef.componentInstance.race = this.race;
    modalRef.componentInstance.event = this.event;
    modalRef.componentInstance.languages = this.languages;
    modalRef.componentInstance.isAdmin = this.isAdmin;
  }

  scrollTable(isScrollRight: boolean): void {
    const table = document.querySelector('.table-responsive') as HTMLElement;
    if (table) {
      const scrollAmount = isScrollRight ? 200 : -200;
      table.scrollBy({left: scrollAmount, behavior: 'smooth'});
    }
  }

  private buildGenderFilterItems(): FilterItem[] {
    return [
      ...(this.genders ?? []).map((item: Lookups) =>
        new FilterItem(item.id, undefined, "app.common.gender." + item.id, false)
      )
    ];
  }

  private buildCategoryFilterGroup(raceId: number): Observable<FilterGroup | undefined> {
    const raceSettings = this.racesMap.get(raceId)?.raceSettings;

    if (raceSettings && !this.showCategories(raceSettings)) {
      return of(undefined);
    }

    const cached = this.categoriesByRaceId.get(raceId);
    if (cached) {
      return of(this.toCategoryFilterGroup(cached));
    }

    return this.competitorResultsService.findCategoryItemsByRaceId(raceId).pipe(
        map(categories => {
            this.categoriesByRaceId.set(raceId, categories);
            return this.toCategoryFilterGroup(categories);
        })
    );
  }

  private toCategoryFilterGroup(categories: CategoryItem[], gender?: number, selectedCategoryId?: number): FilterGroup | undefined {
    const filteredCategories = gender
        ? categories.filter((item: CategoryItem) => item.gender === gender)
        : categories;

    if (filteredCategories.length === 0) return undefined;

    const categoryItems = filteredCategories.map((item: CategoryItem) =>
        new FilterItem(item.id, item.code, undefined, item.id === selectedCategoryId)
    );

    return new FilterGroup('category',
        'category', 'app.common.category',
        FilterGroupType.SINGLE_SELECT, categoryItems.length > 10,
        categoryItems, false, true)
  }


  private showCategories(raceSettings: RaceSettings | undefined): boolean {
    return !!raceSettings?.category && raceSettings?.category?.categoryType !== CategoryType.NOT_USED
  }

  private buildMeasurementTypeFilterItems(raceMeasurementTypes: MeasurementTypes, defaultType: MeasurementTypes): FilterItem[] {
    const isGunPrimary = raceMeasurementTypes === MeasurementTypes.GUN_TIME_PRIMARY;
    const net = new FilterItem(MeasurementTypes.CHIP_TIME_PRIMARY, undefined, 'competitor.table.results.net.time', defaultType === MeasurementTypes.CHIP_TIME_PRIMARY, defaultType === MeasurementTypes.CHIP_TIME_PRIMARY);
    const gross = new FilterItem(MeasurementTypes.GUN_TIME_PRIMARY, undefined, 'competitor.table.results.gross.time', defaultType === MeasurementTypes.GUN_TIME_PRIMARY, defaultType === MeasurementTypes.GUN_TIME_PRIMARY);
    return isGunPrimary ? [gross, net] : [net, gross];
  }

  private buildFilterItemsForRace(race: Race, setActive: boolean, onComplete?: () => void): void {
    if (!this.raceFilterGroup || !race.id) {
      onComplete?.();
      return;
    }

    if (this.filterGroupsMap.get(race.id)) {
      if (setActive) {
        this.setActiveFilter(race.id);
      }
      onComplete?.();
      return;
    }

    this.buildCategoryFilterGroup(race.id).subscribe(categoryGroup => {
      const filterGroups: Map<string, FilterGroup> = new Map<string, FilterGroup>();
      filterGroups.set('race', this.raceFilterGroup!);
      const isTeamRace = RaceSettings.isTeamRace(race.raceSettings);
      if (!isTeamRace) {
        filterGroups.set('gender', new FilterGroup('gender', 'gender', 'app.common.gender',
            FilterGroupType.SINGLE_SELECT, false, this.buildGenderFilterItems(), false, true));
        if (categoryGroup) {
          filterGroups.set('category', categoryGroup);
        }
      }


      if (this.isAdmin || (race.raceSettings?.measurementType === MeasurementTypes.CHIP_TIME_PRIMARY
          || race.raceSettings?.measurementType === MeasurementTypes.GUN_TIME_PRIMARY)) {
        const defaultType = this.isAdmin ? MeasurementTypes.GUN_TIME_PRIMARY : race.raceSettings?.measurementType ?? MeasurementTypes.GUN_TIME_PRIMARY;
        const measurementItems = this.buildMeasurementTypeFilterItems(race.raceSettings?.measurementType ?? MeasurementTypes.GUN_TIME_PRIMARY, defaultType);
        filterGroups.set('measurement',
            new FilterGroup('measurement', 'measurement', 'app.common.race',
                FilterGroupType.SINGLE_SELECT, false, measurementItems, true, true
            )
        );
      }

      this.filterGroupsMap.set(race.id!, filterGroups);
      if (setActive) {
        this.setActiveFilter(race.id!);
      }
      onComplete?.();
    });
  }

  private buildRaceFilterGroup(): FilterGroup | undefined {
    if ((this.event?.racesList?.length ?? 0) > 0) {
      const raceItems = (this.event?.racesList ?? []).map((race: Race) =>
          new FilterItem(race.id,
              getLanguageValue(race.name,
                  race.nameEng, race.nameIta,
                  race.nameSlo, race.nameSr,
                  race.nameBs, this.translocoService.getActiveLang()) ?? "",
                  undefined,
                  this.race?.id === race.id));
      if (raceItems.length > 0) {
        return new FilterGroup(
            'race', 'race', 'app.common.race',
            FilterGroupType.SINGLE_SELECT, false,
            raceItems, true, true
        )
      }
      return undefined;
    }
    return undefined;
  }


  private setActiveFilter(raceId: number) {
    this.activeFilterGroups = this.filterGroupsMap.get(raceId);
    this.activeFilterGroups?.forEach(g => {
      if (g.key !== 'race') {
        g.items.forEach(i => {
          i.selected = i.isDefault
        })
      }
    })
    this.competitorFiler = new CompetitorFilter();
    this.competitorFiler.raceId = raceId;
    this.competitorFiler.measurementType = this.measurementType;
  }

  onFilterChange(result: Record<string, any>): void {
    const {searchTerm, race: raceId, gender, category, measurement: measurementType} = result;
    if (raceId && raceId !== this.race?.id) {
      this.handleRaceSelection(raceId);
    }
    else {
      if (measurementType) {
        this.competitorFiler.measurementType = measurementType;
        this.measurementType = measurementType;
      }
      const normalizedGender = gender ?? undefined;
      const normalizedCurrentGender = this.competitorFiler.gender ?? undefined;
      if (normalizedGender !== normalizedCurrentGender) {
        const selectedCategory = this.competitorFiler.categories?.[0];
        this.competitorFiler.gender = normalizedGender;
        const categoryGroup = this.toCategoryFilterGroup(this.categoriesByRaceId.get(raceId) ?? [], normalizedGender, selectedCategory);
        if (categoryGroup) {
            this.filterGroupsMap.get(raceId)?.set('category', categoryGroup)
            const stillSelected = categoryGroup.items.find(i => i.selected);
            if (stillSelected?.id) {
              this.competitorFiler.categories = [stillSelected.id!];
            }
            else {
              this.competitorFiler.categories = [];
            }
        }
        else {
          this.competitorFiler.categories = [];
          this.filterGroupsMap.get(raceId)?.delete('category');
        }
        this.activeFilterGroups = new Map(this.filterGroupsMap.get(raceId)!);
      }
      else {
        this.competitorFiler.categories = category ? [category] : [];
      }
      this.competitorFiler.name = searchTerm
      this.onExplore(false);
    }
  }

  protected readonly CompetitorStatus = CompetitorStatus;
  protected readonly ResultProcessingType = ResultProcessingType;
  protected readonly FilterGroupType = FilterGroupType;
  protected readonly MIN_SEARCH_INPUT_LENGTH = ResultsTableComponent.MIN_SEARCH_INPUT_LENGTH;
  protected readonly MIN_SEARCH_INPUT_LENGTH_ADMIN = ResultsTableComponent.MIN_SEARCH_INPUT_LENGTH_ADMIN;
  protected readonly Array: ArrayConstructor = Array;
  protected readonly MeasurementTypes = MeasurementTypes;
}
